import type { CaseCopy } from "./types";

export const helpdesk: CaseCopy = {
  slug: "helpdesk",
  displayName: "Helpdesk — Italian Software Support Tickets",
  issueNumberRoman: "IV",
  issueNumberArabic: "04",
  mastheadIssue: "Issue IV · Apr 2026",

  noteTagline: <>What follows is one such reconstruction, applied to a software helpdesk queue.</>,

  sourceCard: (
    <>
      The data comes from an Italian software-management company's <b>internal helpdesk log</b>,
      released under the 4TU benchmark collection. It contains <b>3,804 tickets</b> and roughly
      13,000 events, drawn from a twelve-month window. Each case is one support ticket — opened,
      triaged, taken in charge, resolved, closed. Identifiers are removed; the workflow is real.
      It is a small dataset by volume and a rich one by behaviour: the log is dense with
      hand-offs, waits, and reopenings that a ticket dashboard would never surface.
    </>
  ),

  hero: {
    kicker: "Issue IV  ·  The Ticket That Sat",
    title: (
      <>
        A software company logged <span className="num-amber">3,804</span> support tickets.{" "}
        <span className="frac">The median</span> spent nine days waiting before anyone owned it.
      </>
    ),
    dek: (
      <>
        Support is supposed to be a queue: ticket in, ticket assigned, ticket resolved. The logs
        show a different shape — tickets are assigned fast, then sit idle, then get taken in
        charge, then sit idle again, then close at midnight on a Friday. The customer never saw
        the idle time. The log did.
      </>
    ),
  },

  dotGrid: {
    total: 480,
    bad: 108,
    caption: (
      <>
        <b>1 dot = ~8 tickets</b>
        480 dots represent all 3,804 support tickets from the year. <em>108 dots in red</em>
        — roughly 22% of the log — are the tickets that waited more than ten days between
        being opened and being taken in charge. A queue the team never treated as a queue.
      </>
    ),
  },

  lede: (
    <>
      The team runs a four-step process: <em>Assign seriousness</em>, <em>Take in charge</em>,{" "}
      <em>Resolve</em>, <em>Close</em>. Each step has a name, a form, and a button. The logs
      confirm that every ticket walks the four steps. They also confirm that between the first
      step and the second, the average ticket waits several working days. And that a non-trivial
      share of tickets are closed and then re-opened, weeks later, as if the resolution never
      happened.
    </>
  ),

  headlineFinding: {
    label: "· 01 · Headline finding",
    h2: "The queue is not the bottleneck. The moment before the queue is.",
    body: (
      <>
        Between <em>Assign seriousness</em> and <em>Take in charge ticket</em>, the median wait
        is <b>nine days</b>; the p95 crosses <b>thirty</b>. This is a single transition in the
        workflow and it consumes more wall-clock time than every other step combined. The queue
        itself — from taken-in-charge to resolved — moves quickly. The problem is upstream: a
        ticket is triaged and then becomes invisible until someone, manually, picks it up.
      </>
    ),
    issues: [
      {
        tag: "Issue I · Pre-Queue Wait",
        big: "9",
        unit: "d",
        ev: "Median wait between 'Assign seriousness' and 'Take in charge ticket'. p95 crosses 30 days.",
      },
      {
        tag: "Issue II · Reopen Rate",
        big: "11",
        unit: "%",
        ev: "Share of tickets that are Closed and then reopened — a resolution that did not stick, rediscovered after the fact.",
      },
      {
        tag: "Issue III · Friday Close",
        big: "2.1",
        unit: "×",
        ev: "Across the year's close events, Fridays see twice the close-rate of any other weekday. Queue drain on the clock, not on the work.",
      },
    ],
  },

  sectionTwo: {
    label: "· 02 · The four-step queue vs. the ticket's real life",
    diagram: {
      exhibitTag: (<>Exhibit <b>· As-declared helpdesk queue</b> — with real waits in amber</>),
      sourceTag: "Source: DFG over ~13k events",
      steps: [
        { num: "STEP 01", name: "Open",       subActs: ["Assign seriousness"],              band: "teal"   },
        { num: "STEP 02", name: "Wait",       subActs: ["(idle, pre-owner)"],               band: "muted"  },
        { num: "STEP 03", name: "Take",       subActs: ["Take in charge ticket"],           band: "signal" },
        { num: "STEP 04", name: "Resolve",    subActs: ["Resolve ticket", "Require upgrade"], band: "alert"  },
        { num: "STEP 05", name: "Close",      subActs: ["Closed", "(Reopened)"],            band: "ink"    },
      ],
      gapDays: ["9 d", "0 d", "2 d", "1 d"],
      selfLoops: [{ fromStep: 4, label: "upgrade loop" }],
      topNotes: [
        { text: (<><tspan x="274" dy="0">median <tspan className="n">9 days</tspan> — no owner,</tspan><tspan x="274" dy="18">no customer-visible action</tspan></>), fromStep: 2, tickColor: "muted", width: 240, x: 260, y: 70 },
        { text: (<><tspan className="n">2.1×</tspan> more closes on a Friday</>), fromStep: 5, tickColor: "signal", width: 220, x: 540, y: 86 },
        { text: (<><tspan x="814" dy="0"><tspan className="n">11%</tspan> of closed tickets</tspan><tspan x="814" dy="18">are later reopened</tspan></>), fromStep: 5, tickColor: "alert", width: 220, x: 800, y: 144 },
      ],
      cancelStub: {
        label: "Reopened · 419",
        fromStep: 5,
        note: (<><tspan className="n">419</tspan> tickets closed and re-opened — the same ticket, again</>),
      },
      declaredMedianDays: "12 d",
      legend: [
        { band: "teal",   label: "Open · intake" },
        { band: "muted",  label: "Wait · invisible stall" },
        { band: "signal", label: "Take · ownership" },
        { band: "alert",  label: "Resolve · may loop" },
        { band: "ink",    label: "Close · sometimes temporary" },
      ],
    },
    sparkline: {
      title: "Time between 'Assign seriousness' and 'Take in charge'",
      body: (
        <>
          The queue has no policy, and the distribution shows it. Most tickets are taken quickly;
          a long, soft tail — the <em>invisible backlog</em> — is the whole story of the p95.
        </>
      ),
    },
  },

  sectionThree: {
    labelFromCount: (n) => `· 03 · ${toWord(n)} findings from the queue`,
  },

  sectionFive: {
    label: "· 05 · As a consultant, I'd recommend",
    h2: "Three changes. One policy, one SLA, one dashboard.",
    pull: "Capping the pre-queue wait at 48 hours collapses the 9-day median edge to 2 — the single biggest lever on end-to-end ticket duration.",
    recs: [
      { tag: "R1", act: "Enforce a 48-hour SLA between 'Assign seriousness' and 'Take in charge'.", impact: (<><b>−7 d</b> on that edge</>) },
      { tag: "R2", act: "Block 'Close' on any ticket that was reopened within 30 days — escalate to lead.", impact: (<><b>419</b> reopens / yr in scope</>) },
      { tag: "R3", act: "Show pre-queue age on the triage dashboard, coloured on day 2, red on day 5.", impact: (<><b>makes</b> the backlog visible</>) },
    ],
    close: (
      <>
        Helpdesks do not die of workload. They die of invisible queues. Three small moves make
        the invisible queue visible — and the SLA enforces what the dashboard reveals.
      </>
    ),
  },
};

function toWord(n: number): string {
  const words = ["zero","one","two","three","four","five","six","seven","eight","nine","ten","eleven","twelve"];
  return words[n] ?? String(n);
}
